/**
 * Profile Service Module
 * Handles user profile management including creation, retrieval,
 * updates, and profile photo uploads.
 */

import Parse from "./parse.service";
import { auth } from "services";

const Profile = Parse.Object.extend("Profile");
const user = auth.fetchUser();

/**
 * Retrieves the current user's profile, creates one if it doesn't exist
 * @returns {Promise<Parse.Object>} The user's profile object
 * @throws {Parse.Error} If profile retrieval fails
 */
export const getProfile = async (userId = null) => {
    const user = userId ? userId : auth.fetchUser();
    const query = new Parse.Query(Profile);
    query.equalTo("userId", user);
    query.select("basic");
    query.select("credits")
    try {
        let profile = await query.first();
        if (!profile) {
            profile = new Profile();
            profile.set("userId", user);
            await profile.save();
        }
        return profile;
    } catch (err) {
        throw err;
    }
};

export const getProfileBasics = async (userId) => {
    const profile = await getProfile(userId);
    return profile?.get("basic")
}

export const updateProfileBasics = async (data) => {
    try {
        const profile = await getProfile();
        profile.set("basic", {
            ...profile?.get("basic"),
            ...data
        });
        if (data?.contact?.email) {
            await auth.updateEmail(data.contact.email);
        }
        const res = await profile.save();
        return res?.get("basic")
    } catch (error) {
        console.error("Error updating profile:", error);
    }
}

/**
 * Updates a user preference key in basic.preferences
 * @param {string} key - Preference key (e.g., 'khataTxnView')
 * @param {any} value - Preference value (e.g., 'table' or 't-view')
 * @returns {Promise<Object>} Updated basic profile object
 */
export const updateUserPreference = async (key, value) => {
    try {
        const profile = await getProfile();
        const currentBasic = profile?.get("basic") || {};
        const preferences = {
            ...(currentBasic.preferences || {}),
            [key]: value
        };
        profile.set("basic", {
            ...currentBasic,
            preferences
        });
        const res = await profile.save();
        return res?.get("basic");
    } catch (error) {
        console.error("Error updating user preference:", error);
    }
};

/**
 * Updates the user's onboarding status
 * @param {boolean} status - New onboarding status
 * @returns {Promise<Parse.User>} Updated user object
 */
export function setOnboardingInfo(status) {
    user.set('onboardingInfo', status)
    return user.save();
}

/**
 * Sets the type of business for the user
 * @param {string} typeOfBusiness - Type of business to set
 * @returns {Promise<Parse.User>} Updated user object
 */
export function setTypeOfBusiness(typeOfBusiness) {
    user.set('typeOfBusiness', typeOfBusiness)
    return user.save();
}